/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import { InputLabel, useMediaQuery } from '@material-ui/core';
import { compose } from 'recompose';
import Typography from '@material-ui/core/Typography';
import ReactMarkdown from 'react-markdown';
import { withStyles } from '@material-ui/core/styles';
import Checkbox from '@material-ui/core/Checkbox';
import {
    object, shape, string, arrayOf, number, bool, func,
} from 'prop-types';
import { useSelector } from 'react-redux';
import { ChevronDown, ChevronUp } from 'lucide-react';
import SubscriptionPDP from '../../../SubscriptionInformation/SubscriptionPDP';
import SubscriptionDropDown from '../SubscriptionDropDown';
import { getSSRDeviceType } from '../../../../../../state/ducks/App/App-Selectors';
import useIsPlaPage from '../../../../../helpers/hooks/useIsPlaPage';

const styles = (theme) => ({
    subscriptionContainer: {
        border: `1px solid ${(theme.palette.grey8)}`,
        borderRadius: '4px',
        padding: '9px 14px',
        [theme.breakpoints.down('sm')]: {
            padding: '9px 5px',
        },
    },
    marginBottom15: {
        marginBottom: '15px',
    },
    subContainer: {
        display: 'flex',
        maxWidth: '100%',
        '& > div': {
            margin: '0px 10px',
            [theme.breakpoints.down('1330')]: {
                margin: '0px',
            },

        },
        [theme.breakpoints.down('xs')]: {
            '& > div': {
                width: '100%',
            },
        },
    },
    [theme.breakpoints.down('xs')]: {
        subContainerSm: {
            marginTop: 15,
        },
    },
    icon: {
        color: theme?.palette?.cms?.icon,
        margin: 'auto',
    },
    borderRight1: {
        [theme.breakpoints.up('sm')]: {
            borderRight: `1px solid ${(theme.palette.grey8)}`,
        },
    },
    marginTopBottom10: {
        margin: '10px 0px',
    },
    subcriptionContent: {
        '& p': {
            fontSize: 16,
            margin: 4,
            [theme.breakpoints.down('xs')]: {
                margin: 2,
            },
            [theme.breakpoints.down('sm')]: {
                fontSize: 15,
            },
        },
    },
    fontSize67: {
        fontSize: 64,
        [theme.breakpoints.down('md')]: {
            fontSize: 45,
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: 35,
        },
        [theme.breakpoints.down('xs')]: {
            paddingLeft: 4,
            fontSize: 38,
        },

    },
    fontSize34: {
        fontSize: '34px',
    },
    subscriptionCheckBoxContent: {
        fontSize: 14,
        '& b': {
            fontSize: 16,
            display: 'block',
        },
    },
    checkbox: {
        color: theme.palette.cms?.checkbox,
        height: '100%',
        [theme.breakpoints.down('sm')]: {
            height: 'auto',
            marginTop: '5px',
            '& span svg': {
                fontSize: '24px',
            },
        },
        '&$checked': {
            color: theme.palette.cms?.checkbox,
            '&:hover': {
                backgroundColor: theme.palette.colorNeutral20,
            },
        },
        '&:hover': {
            backgroundColor: theme.palette.colorNeutral20,
        },
    },
    checked: {
        color: theme.palette.cms?.checkbox,
        '&$checked': {
            color: theme.palette.cms?.checkbox,
            '&:hover': {
                backgroundColor: theme.palette.colorNeutral20,
            },
        },
    },
    plaCheckbox: {
        [theme.breakpoints.up('1280')]: {
            width: '100%',
            padding: 0,
        },
    },
    marginNone: {
        '& p': {
            margin: 0,
        },
    },
    inline: {
        display: 'inline-block',
    },
    maxWidth: {
        maxWidth: '100%',
    },
    alignImage: {
        alignItems: 'center',
    },
    paddingLeft: {
        paddingLeft: '5px',
        [theme.breakpoints.down('375')]: {
            paddingLeft: '0px',
        },
    },
    alignSpan: {
        alignItems: 'center',
        [theme.breakpoints.up('sm')]: {
            paddingRight: '5px',
            borderRight: `1px solid ${(theme.palette.grey8)}`,
        },
        [theme.breakpoints.down('sm')]: {
            marginBottom: '15px',
            marginLeft: '2px',
        },
    },
    subscriptionDisplay: {
        display: 'none',
    },
    [theme.breakpoints.up('1225')]: {
        outerSubscriptionContainer: {
            flexWrap: 'nowrap',
        },
    },
    subscriptionWrapper: {
        [theme.breakpoints.up('600')]: {
            flexWrap: 'wrap',
            display: 'flex',
            alignItems: 'flex-start',
            '& $outerContainer': {
                flexWrap: 'wrap',
                display: 'flex',
                alignItems: 'flex-start',
                padding: '0px 16px 0px 10px',
            },
            '& $subContainer': {
                flexBasis: '100%',
                maxWidth: '100%',
                borderRight: '0px',
                paddingRight: '16px',
            },
            '& $subcriptionContent': {
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                margin: '0px',
                '& p': {
                    fontSize: '12px',
                    lineHeight: '18px',
                    fontWeight: 'normal ',
                },
            },
            '& $paddingLeft': {
                padding: '0px',
            },
        },
        '& $alignSpan': {
            margin: '0px 0px 8px',
        },
        '& $checkbox': {
            padding: '0px 16px',
            marginTop: '0px',
            '& span svg': {
                fill: '#9D9D9D',
            },
        },
        '& $subscriptionHeader': {
            fontSize: '18px',
            lineHeight: '20px',
            margin: '3px 0px 4px',
            '& strong': {
                fontWeight: '700',
                fontFamily: 'museo-sans, "Area Variable", LatoBold',
            },
            [theme.breakpoints.down('600')]: {
                fontSize: '16px',
            },
        },
        '& $subscriptionCopy': {
            fontSize: '14px',
            lineHeight: '22px',
        },
        '& $paddingLeft': {
            paddingLeft: '0px',
        },
        [theme.breakpoints.down('400')]: {
            '& $gridContainer': {
                maxWidth: '80%',
                flexBasis: '80%',
            },
            '& $checkbox': {
                padding: '0px 12px 0px 16px',
            },
        },
    },
    gridContainer: {},
    subscriptionCopy: {},
    subscriptionHeader: {},
    subscribeDetail: {
        textDecoration: 'underline',
        textDecorationThickness: '1px',
        textUnderlineOffset: '3px',
        fontSize: '12px',
        lineHeight: '14.4px',
        alignSelf: 'flex-start',
        cursor: 'pointer',
        margin: '4px 0px 0px',
        letterSpacing: '0.5px',
        display: 'flex',
        '& p': {
            margin: '0px',
            padding: '0px',
        },
        [theme.breakpoints.up('600')]: {
            lineHeight: '20px',
        },
        '& svg': {
            marginLeft: '8px',
            [theme.breakpoints.down('600')]: {
                width: '20px',
                height: '20px',
            },
        },
    },
    outerContainer: {},
    pdpShortStyle: {
        '& $alignSpan': {
            margin: '0px',
        },
    },
});

const AbVariantSubscription = ({
    classes,
    brand,
    subscriptionCopy,
    subscriptionDetailsFrequencyValue,
    subscriptionDetailsDurationValue,
    subscriptionConfig,
    isShowingFrequencyDropdown,
    isSubscriptionDurationListEnabled,
    selectValue,
    selectedSku,
    handleChange,
    durationValue,
    subscriptionCheckbox,
    triggerSubscriptionCheck,
    pdpOfferContent,
    isPdpRedesignEnabled,
    isPdpRedesignShortEnabled,
}) => {
    const isSmallDevice = useMediaQuery((theme) => theme?.breakpoints.down('sm'));
    const ssrDeviceType = useSelector(getSSRDeviceType);
    const hasIcon = subscriptionCopy?.icon?.url || null;
    const isPlaPage = useIsPlaPage();
    const [open, close] = useState(false);

    let pdpReskinStyle = '';
    if (isPdpRedesignShortEnabled) {
        pdpReskinStyle = `${classes.subscriptionWrapper} ${classes.pdpShortStyle}`;
    } else if (isPdpRedesignEnabled) {
        pdpReskinStyle = classes.subscriptionWrapper;
    }
    useEffect(() => {
        close(subscriptionCheckbox);
    }, [subscriptionCheckbox]);

    const renderIconUI = () => hasIcon && (
        <Grid item xs={2} sm={1} className={`${classes.subContainer} ${classes.alignImage}`}>
            <img className={classes.maxWidth} src={subscriptionCopy?.icon?.url} alt="subscription-icon" />
        </Grid>
    );
    const subscribeDetailToggle = () => {
        close(!open);
    };

    const renderSingleLineCopy = () => {
        const selectedFrequency = subscriptionConfig?.subscription_interval?.[selectValue];
        const defaultFrequency = subscriptionConfig?.subscription_interval?.[subscriptionDetailsFrequencyValue] || subscriptionDetailsFrequencyValue;
        if (subscriptionCopy?.single_line_copy?.toLowerCase?.().trim?.() === 'n/a') {
            return '';
        }
        if (subscriptionCopy?.single_line_copy) {
            return <ReactMarkdown className={`${classes.marginNone} ${classes.subscriptionCopy}`} source={subscriptionCopy?.single_line_copy} />;
        }
        return `Start a subscription and have it delivered ${selectedFrequency || defaultFrequency}`;
    };
    const renderSubscriptionDropDown = () => (
        <Grid item lg={hasIcon ? 6 : 5} xl={hasIcon ? 6 : 5} md={hasIcon ? 6 : 5} xs={hasIcon ? 10 : 12} sm={hasIcon ? 10 : 12} className={`${classes.subContainer} ${classes.paddingLeft}`}>
            <div className={classes.subcriptionContent}>
                {isShowingFrequencyDropdown ? (
                    <SubscriptionDropDown
                        selectValue={selectValue}
                        subscriptionsList={selectedSku.subscriptionsList}
                        fieldLabel={subscriptionCopy?.frequency_label ? <ReactMarkdown className={`${classes.marginNone} ${classes.inline}`} source={subscriptionCopy?.frequency_label} /> : 'Frequency:'}
                        handleChange={handleChange}
                        isPdpRedesignEnabled={isPdpRedesignEnabled}
                    />
                ) : (
                    <Typography variant="body1">
                        {subscriptionCopy?.frequency_label ? <ReactMarkdown className={`${classes.marginNone} ${classes.inline}`} source={subscriptionCopy?.frequency_label} /> : (<strong>Frequency:</strong>)} {subscriptionConfig.subscription_interval[subscriptionDetailsFrequencyValue] || subscriptionDetailsFrequencyValue}
                    </Typography>
                )}
                {isSubscriptionDurationListEnabled ? (
                    <>
                        <SubscriptionDropDown
                            selectValue={durationValue}
                            subscriptionsList={selectedSku.subscriptionsList}
                            handleChange={handleChange}
                            fieldLabel={subscriptionCopy?.duration_label ? <ReactMarkdown className={`${classes.marginNone} ${classes.inline}`} source={subscriptionCopy?.duration_label} /> : 'Duration:'}
                            dropdownType="subscription_duration"
                            frequencyOrDuration="duration"
                            isPdpRedesignEnabled={isPdpRedesignEnabled}
                        />
                    </>
                ) : (
                    <Typography variant="body1">
                        {subscriptionCopy?.duration_label ? <ReactMarkdown className={`${classes.marginNone} ${classes.inline}`} source={subscriptionCopy?.duration_label} /> : (<strong>Duration:</strong>)}  {subscriptionConfig.subscription_duration[subscriptionDetailsDurationValue] || subscriptionDetailsDurationValue}
                    </Typography>
                )}
            </div>
            <Typography variant="span" className={`${classes.marginTopBottom10}`} />
        </Grid>
    );
    return (
        <Grid container className={`${!subscriptionCheckbox ? classes.outerSubscriptionContainer : ''} ${pdpReskinStyle}`}>
            <Grid item sm={2} md={1} lg={1} xl={1}>
                <InputLabel
                    htmlFor="chk-monthly-subscription"
                    className={classes.subscriptionDisplay}
                >
                    Monthly Subscription
                </InputLabel>
                <Checkbox
                    id="chk-monthly-subscription"
                    data-test="pdp-check-monthly-subscription"
                    data-testid="pdp-check-monthly-subscription"
                    name="monthlySubscriptionChecked"
                    checked={subscriptionCheckbox}
                    onChange={triggerSubscriptionCheck}
                    classes={{
                        root: isPlaPage ? `${classes.checkbox} ${classes.plaCheckbox}` : classes.checkbox,
                        checked: classes.checked,
                    }}
                />
                {isSmallDevice && hasIcon && (
                    <img className={classes.maxWidth} src={subscriptionCopy?.icon?.url} alt="subscription-icon" />
                )}
            </Grid>
            <Grid xs={10} sm={10} md={11} lg={11} xl={11} className={classes.gridContainer}>
                <Grid container className={`${classes.outerSubscriptionContainer} ${classes.outerContainer} `}>
                    <Grid item xs={12} lg={5} xl={5} md={5} sm={12} className={`${classes.subContainer} ${classes.alignSpan} ${classes.subContainerSm}`}>
                        <Typography variant="body1" className={`${classes.subscriptionCheckBoxContent}`}>
                            {subscriptionCopy?.single_line_label ? <ReactMarkdown className={`${classes.marginNone} ${classes.subscriptionHeader}`} source={subscriptionCopy?.single_line_label} /> : (<><strong>Make this a recurring Shipment</strong><br /></>)}
                            {renderSingleLineCopy()}
                        </Typography>
                    </Grid>
                    {!isSmallDevice && renderIconUI()}
                    {(ssrDeviceType === 'mobile' || isPdpRedesignShortEnabled) ? (
                        subscriptionCheckbox &&  renderSubscriptionDropDown()
                    ) : renderSubscriptionDropDown()}
                    {isPdpRedesignEnabled && (
                        <div role="presentation" className={classes.subscribeDetail} onClick={() => subscribeDetailToggle()}>
                            {subscriptionCopy?.pdp_dropdown_trigger_copy || 'Why Subscribe?'} {open ? <ChevronUp  color="#2F363D" width={24} height={24} /> : <ChevronDown  color="#2F363D" width={24} height={24} />}
                        </div>
                    )}
                </Grid>
            </Grid>
            {((subscriptionCheckbox && !isPdpRedesignEnabled) || (open && isPdpRedesignEnabled)) && (
                <SubscriptionPDP
                    copy={subscriptionCopy?.offer_copy || ''}
                    brand={brand}
                    isPdpRedesignEnabled={isPdpRedesignEnabled}
                />
            )}
        </Grid>
    );
};

AbVariantSubscription.propTypes = {
    classes: object.isRequired,
    subscriptionCopy: shape({
        single_line_label: string,
        frequency_label: string,
        duration_label: string,
    }),
    subscriptionDetailsFrequencyValue: number.isRequired,
    subscriptionDetailsDurationValue: number.isRequired,
    subscriptionConfig: shape({
        subscription_duration: arrayOf(number),
        subscription_interval: arrayOf(number),
    }).isRequired,
    isShowingFrequencyDropdown: bool,
    brand: shape({
        domain: string,
    }).isRequired,

    isSubscriptionDurationListEnabled: bool.isRequired,
    selectValue: string.isRequired,
    selectedSku: shape({
        subscriptionsList: shape({
            type: arrayOf(string),
        }),
    }).isRequired,
    handleChange: func.isRequired,
    durationValue: string.isRequired,
    subscriptionCheckbox: bool.isRequired,
    triggerSubscriptionCheck: func.isRequired,
    pdpOfferContent: object,
    isPdpRedesignEnabled: bool,
    isPdpRedesignShortEnabled: bool,
};

AbVariantSubscription.defaultProps = {
    subscriptionCopy: {},
    isShowingFrequencyDropdown: false,
    pdpOfferContent: {},
    isPdpRedesignEnabled: false,
    isPdpRedesignShortEnabled: false,
};

const enhance = compose(
    withStyles(styles),
);

export default enhance(AbVariantSubscription);
